import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_BG =
    "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBjb25zdHJ1Y3Rpb24lMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4MzY5MzA1NXww&ixlib=rb-4.1.0&q=85";

const PHONE_DISPLAY = "+41 44 000 00 00";
const PHONE_HREF = "+41440000000";
const WA_NUMBER = "41440000000"; // no plus
const EMAIL = "info@clerbau.ch";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Bitte Name, E-Mail und Nachricht ausfüllen.");
            return;
        }
        try {
            setLoading(true);
            const { data } = await axios.post(`${API}/contact`, form);
            toast.success(data.message || "Ihre Anfrage wurde übermittelt.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Anfrage konnte nicht gesendet werden.";
            toast.error(String(msg));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="kontakt"
            data-testid="contact-section"
            className="relative min-h-screen overflow-hidden bg-[#14100D] text-[#FEFEFE]"
        >
            {/* Background image */}
            <div className="absolute inset-0">
                <img
                    src={CONTACT_BG}
                    alt=""
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-[#14100D]/85" />
            </div>

            <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40 grid grid-cols-1 lg:grid-cols-12 gap-16">
                {/* Left: info */}
                <div className="lg:col-span-5 flex flex-col">
                    <div className="eyebrow mb-6" style={{ color: "#E20514" }}>Kontakt</div>
                    <h2
                        className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.02]"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        Bereit für Ihr
                        <br />
                        <span className="font-medium">nächstes Bauwerk?</span>
                    </h2>
                    <p className="mt-8 max-w-md text-[15px] lg:text-[17px] leading-relaxed text-white/75">
                        Erzählen Sie uns von Ihrem Vorhaben. Wir melden uns innerhalb eines Werktags mit einer ehrlichen Einschätzung.
                    </p>

                    <div className="mt-12 space-y-6">
                        <ContactRow icon={Phone} label="Telefon" value={PHONE_DISPLAY} href={`tel:${PHONE_HREF}`} testid="contact-phone" />
                        <ContactRow icon={Mail} label="E-Mail" value={EMAIL} href={`mailto:${EMAIL}`} testid="contact-email" />
                        <ContactRow icon={MapPin} label="Adresse" value="Talstrasse 1, 8852 Altendorf" testid="contact-address" />
                    </div>

                    <a
                        href={`https://wa.me/${WA_NUMBER}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-whatsapp"
                        className="md:hidden mt-10 inline-flex items-center gap-3 px-6 py-4 border border-white/30 text-[12px] tracking-[0.24em] uppercase font-semibold hover:border-[#E20514] hover:text-[#E20514] transition-colors"
                        style={{ borderRadius: 2 }}
                    >
                        <MessageCircle size={16} /> WhatsApp Direktkontakt
                    </a>

                    {/* Map placeholder */}
                    <div className="mt-12 hidden lg:block">
                        <div
                            className="relative aspect-[16/9] border border-white/15"
                            data-testid="map-placeholder"
                            style={{
                                backgroundImage:
                                    "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
                                backgroundSize: "24px 24px",
                            }}
                        >
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="flex items-center gap-3 text-white/70 text-[11px] tracking-[0.28em] uppercase">
                                    <MapPin size={14} className="text-[#E20514]" />
                                    Altendorf, Schweiz
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: form */}
                <div className="lg:col-span-6 lg:col-start-7">
                    <form
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2"
                    >
                        <Field label="Name" required>
                            <input
                                data-testid="contact-input-name"
                                className="cler-input"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Ihr Name"
                                required
                            />
                        </Field>

                        <Field label="E-Mail" required>
                            <input
                                type="email"
                                data-testid="contact-input-email"
                                className="cler-input"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="ihre.email@beispiel.ch"
                                required
                            />
                        </Field>

                        <Field label="Telefon">
                            <input
                                data-testid="contact-input-phone"
                                className="cler-input"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="+41 79 000 00 00"
                            />
                        </Field>

                        <Field label="Betreff">
                            <input
                                data-testid="contact-input-subject"
                                className="cler-input"
                                value={form.subject}
                                onChange={update("subject")}
                                placeholder="z.B. Fassadensanierung"
                            />
                        </Field>

                        <div className="col-span-1 sm:col-span-2">
                            <Field label="Nachricht" required>
                                <textarea
                                    data-testid="contact-input-message"
                                    className="cler-input"
                                    rows={4}
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="Beschreiben Sie kurz Ihr Vorhaben…"
                                    required
                                />
                            </Field>
                        </div>

                        <div className="col-span-1 sm:col-span-2 mt-8 flex flex-wrap items-center gap-4">
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit"
                                className="inline-flex items-center gap-3 px-8 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold disabled:opacity-60"
                                style={{
                                    background: "#E20514",
                                    color: "#FEFEFE",
                                    borderRadius: 2,
                                    transition: "background-color 0.35s ease",
                                }}
                                onMouseOver={(e) => (e.currentTarget.style.background = "#FEFEFE") || (e.currentTarget.style.color = "#14100D")}
                                onMouseOut={(e) => (e.currentTarget.style.background = "#E20514") || (e.currentTarget.style.color = "#FEFEFE")}
                            >
                                {loading ? "Wird gesendet…" : "Jetzt Offerte anfragen"}
                                <ArrowRight size={16} />
                            </button>
                            <span className="text-[12px] tracking-[0.18em] uppercase text-white/50">
                                Antwort innerhalb 24h
                            </span>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block mt-6">
            <span className="block text-[11px] tracking-[0.24em] uppercase text-white/50 mb-1">
                {label}{required && <span className="text-[#E20514]"> *</span>}
            </span>
            {children}
        </label>
    );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
    const Content = (
        <div className="flex items-start gap-4 group">
            <Icon size={18} className="text-[#E20514] mt-1 flex-shrink-0" />
            <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-1">
                    {label}
                </div>
                <div className="text-[16px] group-hover:text-[#E20514] transition-colors">
                    {value}
                </div>
            </div>
        </div>
    );
    if (href) {
        return (
            <a href={href} data-testid={testid} className="block">
                {Content}
            </a>
        );
    }
    return <div data-testid={testid}>{Content}</div>;
}
